import React, { Fragment, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import { Card, CardContent, Grid } from "@mui/material"
import type { InputChangeEvent } from "@synapse-analytics/synapse-ui"
import { Button, InputText } from "@synapse-analytics/synapse-ui"
import { useFormikContext } from "formik"

import { InfoBlock } from "../../components/containers/InfoBlock"
import { BaseSimpleDialog } from "../../components/dialogs/BaseSimpleDialog"
import { FeaturesMenu } from "../../components/form/FeaturesMenu"
import { useDebounce } from "../../hooks/useDebounce"
import type { RootState } from "../../store/ReduxStore"
import { WorkflowInputFeature } from "../../types/generated/api/WorkflowInputFeature"
import { getEnvVar } from "../../utils/genericHelpers"
import { FILE_ID_MATCHER } from "../../utils/rulesetHelpers"
import { ArrowAdornment } from "../Scorecards/components/Condition"
import type { FeatureData, SchemaFeature } from "../Workflows"
import { useWorkflowFeatures } from "../Workflows/utils/workflowHelpers"
import { Condition } from "./Condition"
import {
  AddToBuckets,
  createScoreTableBucketsFromResultsArray,
  getIndexRepresentationBasedOnCriteria,
  mapFeatureTypeToConditionType,
  reverseIndexRepresentationBasedOnCriteria,
} from "./helpers"
import type { ConfigCardProps, ScoreTableFormik } from "./interfaces"

import styles from "./ScoreTables.module.scss"

export function ConfigCard(props: Readonly<ConfigCardProps>): React.ReactElement {
  const { index, mode } = props

  const maxBucketsFeatureFlags = Number(getEnvVar("RULES_TABLES_MAXIMUM_BUCKETS"))

  const { t } = useTranslation("scorecards")

  const { values, setFieldValue, handleChange, handleBlur, setValues, isSubmitting } =
    useFormikContext<ScoreTableFormik>()

  // variable contains all features (schema, computed, constant)
  const allFeatures = useWorkflowFeatures(new Set(["SchemaFeatures", "ComputedFeatures", "ConstantFeatures"]))

  const workflowsList = useSelector((state: RootState) => state?.workflow?.workflows)

  const filteredFeatures = useMemo(() => {
    const filteredFeatures = allFeatures.filter((item: FeatureData | SchemaFeature) =>
      item?.name?.toLowerCase()?.includes(values.features[index]?.toLowerCase() ?? ""),
    )

    return [...filteredFeatures]

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.features[index]])

  const [isDeletionDialogOpen, setIsDeletionDialogOpen] = useState<null | boolean>(null)

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  // ref for LHS feature
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const inputRef = useRef<any>()

  // Check if current feature is duplicate (for real-time feedback)
  const checkDuplicateFeature = (features: string[], index: number): boolean => {
    const currentFeature = features[index]?.trim()
    if (!currentFeature) return false

    const counts = new Map()
    for (const feature of features) {
      const normalized = feature?.trim()
      if (normalized) {
        counts.set(normalized, (counts.get(normalized) || 0) + 1)
      }
    }

    return counts.get(currentFeature) > 1
  }

  const debouncedOnChange = useDebounce((e: InputChangeEvent): void => {
    setFieldValue(`features.${index}`, e.target.value)

    const tempCriteria = values.criteria

    tempCriteria[index]?.forEach((condition) => {
      condition.feature = e.target.value as string
    })

    setFieldValue("criteria", tempCriteria)

    setAnchorEl(inputRef?.current)
    setTimeout(() => {
      inputRef?.current?.focus()
    }, 100)
  }, 400)

  const handleFeatureChange = (event: InputChangeEvent): void => {
    handleChange(event)
    setAnchorEl(null)
    debouncedOnChange(event)
  }

  const handleFeatureSelectionFromMenu = (selectedItem: FeatureData): void => {
    const newFeatures = [...values.features]
    newFeatures[index] = selectedItem?.name

    const newCriteria = [...values.criteria]
    newCriteria[index]?.forEach((condition) => {
      condition.feature = selectedItem?.name
      condition.type = mapFeatureTypeToConditionType(selectedItem?.type)
    })

    // Single state update to update everything at once, prevents validation running on partially updated state
    setValues({
      ...values,
      features: newFeatures,
      criteria: newCriteria,
    })
  }

  const handleAddNewCriteria = (): void => {
    const buckets = createScoreTableBucketsFromResultsArray(values.results as number[], values.criteria)

    AddToBuckets(buckets, index)

    setFieldValue("results", buckets.flat(values.criteria.length))

    const currentFeature = allFeatures.find((feature) => feature.name === values.features[index])

    setFieldValue(`criteria.[${index}]`, [
      ...(values.criteria[index] || []),
      {
        feature: values.features[index],
        condition: `${values.features[index]} = `,
        parsedCondition: [values.features[index], "=", ""],
        type: mapFeatureTypeToConditionType(currentFeature?.type || "string"),
        operator: "equal",
        value: "",
      },
    ])
  }
  const handleDeleteBucket = (): void => {
    setFieldValue("results", [])

    setValues((prev) => {
      const deletedFilesIds = prev.criteria[index]
        ?.filter((item) => item.value?.includes("file"))
        .map((item) => item.value?.match(FILE_ID_MATCHER)?.[1])

      prev.criteria_files = prev.criteria_files.filter((file) => !deletedFilesIds?.includes(file.uuid))

      prev.criteria = prev.criteria.filter((_, i: number) => i !== index)
      prev.features = prev.features.filter((_, i: number) => i !== index)

      return { ...prev }
    })

    setIsDeletionDialogOpen(null)
  }

  // TODO:: refactor
  const handleConfigCardReordering = (direction: "up" | "down"): void => {
    const swappingIndex = direction === "up" ? index - 1 : index + 1
    const criteria = values.criteria

    const criteria_new = [...values.criteria]

    if (!criteria_new[index] || !criteria_new[swappingIndex]) return
    ;[criteria_new[index], criteria_new[swappingIndex]] = [criteria_new[swappingIndex], criteria_new[index]]

    const results_new = Array(values.results.length).fill(null)

    for (let i = 0; i < values.results.length; i++) {
      const J = []

      // Generating index representation based on criteria
      for (let j = 0; j < criteria.length; j++) {
        J.push(getIndexRepresentationBasedOnCriteria(i, criteria, j))
      }

      if (!J[index] || !J[swappingIndex]) {
        continue
      }

      //  Swapping criterions
      ;[J[index], J[swappingIndex]] = [J[swappingIndex], J[index]]

      // Reversing index representation based on generated representation and new criteria

      let new_i: number[] | number = []
      for (let j = 0; j < J.length; j++)
        new_i.push(reverseIndexRepresentationBasedOnCriteria(J[j] as number, criteria_new, j))

      new_i = new_i.reduce((acc, i) => acc + i, 0)

      results_new.splice(new_i, 1, values.results[i])
    }

    setFieldValue("results", results_new)

    setFieldValue("criteria", criteria_new)

    const features = values.features

    if (typeof features[index] !== "string" || typeof features[swappingIndex] !== "string") return
    ;[features[index], features[swappingIndex]] = [features[swappingIndex], features[index]]
    setFieldValue("features", features)
  }

  return (
    <Fragment>
      {!!isDeletionDialogOpen && (
        <BaseSimpleDialog
          open={true}
          isLoading={false}
          name={""}
          onAccept={handleDeleteBucket}
          onClose={() => setIsDeletionDialogOpen(null)}
          mode={"delete-config"}
        />
      )}

      <Card className={styles.card}>
        <CardContent className={styles.cardContent}>
          <Grid container item xs={12} gap={1}>
            <Grid container item gap={1} display={"flex"} flexWrap={"nowrap"}>
              {mode !== "read" && (
                <Fragment>
                  <Button
                    variant="ghost"
                    size="regular"
                    disabled={index === 0}
                    onClick={() => handleConfigCardReordering("up")}
                  >
                    <KeyboardArrowUpIcon sx={{ fontSize: "22px" }} />
                  </Button>

                  <Button
                    variant="ghost"
                    size="regular"
                    disabled={index === values.criteria.length - 1}
                    onClick={() => handleConfigCardReordering("down")}
                  >
                    <KeyboardArrowDownIcon sx={{ fontSize: "22px" }} />
                  </Button>
                </Fragment>
              )}
              {mode === "read" ? (
                <InfoBlock text={values.features[index] ?? ""} type="default" />
              ) : (
                <Fragment>
                  <InputText
                    key={`features.${index}`}
                    id={`features.${index}`}
                    anchoringRef={inputRef}
                    variant="filled"
                    fullWidth
                    hideDescription
                    value={values.features[index]}
                    handleChange={handleFeatureChange}
                    handleBlur={handleBlur}
                    placeholder={t("inputFeaturePlaceholder")}
                    endAdornment={
                      allFeatures?.length > 0 ? (
                        <ArrowAdornment
                          anchorEl={anchorEl}
                          onClick={() => setAnchorEl(!!anchorEl ? null : inputRef?.current)}
                        />
                      ) : undefined
                    }
                    error={checkDuplicateFeature(values.features, index)}
                  />

                  {/* as far as i remember it was made to accommodate MUI, so maybe we don't need it -> 9/2/2023 */}
                  {/* TODO:: Refactor and discuss removing adding to schema from this Menu */}
                  <FeaturesMenu
                    filteredFeatures={filteredFeatures}
                    key={`${filteredFeatures?.length}`}
                    workflowsList={workflowsList}
                    anchorEl={anchorEl}
                    queryKey={values.features[index] ?? ""}
                    handleFeatureTypeChange={() => {
                      return false
                    }}
                    onAddingNewFeature={() => {
                      return false
                    }}
                    onSelectingFeature={(item) => {
                      handleFeatureSelectionFromMenu(item)
                      setAnchorEl(null)
                    }}
                    onMenuClose={() => setAnchorEl(null)}
                    initialFeatureType={() => WorkflowInputFeature.type.NUMBER}
                  />

                  <Button
                    variant="dangerous"
                    onClick={() => setIsDeletionDialogOpen(true)}
                    disabled={isSubmitting || values.criteria.length <= 2}
                    tooltip={values.criteria.length <= 2 ? t("featuresPerTableToolTip") : undefined}
                  >
                    {t("deleteBtn")}
                  </Button>
                </Fragment>
              )}
            </Grid>

            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            {values.criteria[index]?.map((_: any, idx: number) => (
              <Condition mode={mode} configIndex={index} conditionIndex={idx} />
            ))}
          </Grid>

          {mode !== "read" && (
            <Grid item xs={12} mt={1}>
              <Button
                variant="ghost"
                onClick={handleAddNewCriteria}
                disabled={
                  values?.criteria?.[index] &&
                  values.criteria[index].length >= maxBucketsFeatureFlags &&
                  maxBucketsFeatureFlags !== -1
                }
                tooltip={
                  values?.criteria?.[index] &&
                  values.criteria[index].length >= maxBucketsFeatureFlags &&
                  maxBucketsFeatureFlags !== -1
                    ? t("maxConditionToolTip", {
                        maxNum: maxBucketsFeatureFlags,
                      })
                    : undefined
                }
              >
                + {t("addConditionBtn")}
              </Button>
            </Grid>
          )}
        </CardContent>
      </Card>
    </Fragment>
  )
}
